import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Bank, Plus, Funnel, MagnifyingGlass, ArrowDown, ArrowUp,
    PencilSimple, Trash, Eye, EyeSlash, DownloadSimple,
} from "@phosphor-icons/react";
import { api, formatApiError, API_BASE, getToken } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import StatusBadge from "../components/StatusBadge";
import BankTransactionDialog from "../components/BankTransactionDialog";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
    AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const DIR_STYLE = {
    in: "text-emerald-700",
    out: "text-rose-700",
    neutral: "text-slate-600",
};
const DIR_PREFIX = { in: "+", out: "−", neutral: "" };

export default function BankTransactions() {
    const { user } = useAuth();
    const canEdit = user?.role === "super_admin";

    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ count: 0, inflow: 0, outflow: 0, net: 0 });
    const [meta, setMeta] = useState({ txn_types: [], payment_modes: [], categories: [] });
    const [loading, setLoading] = useState(true);

    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [modeFilter, setModeFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [includeDeleted, setIncludeDeleted] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteReason, setDeleteReason] = useState("");
    const [deleting, setDeleting] = useState(false);

    const params = useMemo(() => {
        const p = {};
        if (q.trim()) p.q = q.trim();
        if (typeFilter) p.txn_type = typeFilter;
        if (modeFilter) p.payment_mode = modeFilter;
        if (categoryFilter) p.category = categoryFilter;
        if (dateFrom) p.date_from = dateFrom;
        if (dateTo) p.date_to = dateTo;
        if (includeDeleted) p.include_deleted = true;
        return p;
    }, [q, typeFilter, modeFilter, categoryFilter, dateFrom, dateTo, includeDeleted]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/billing/bank-transactions", { params });
            setItems(data.items || []);
            setSummary(data.summary || { count: 0, inflow: 0, outflow: 0, net: 0 });
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load transactions"));
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/billing/bank-transactions/meta");
                setMeta(data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load metadata"));
            }
        })();
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 200);  // small debounce on q changes
        return () => clearTimeout(t);
    }, [load]);

    const openCreate = () => { setEditTarget(null); setDialogOpen(true); };
    const openEdit = (txn) => { setEditTarget(txn); setDialogOpen(true); };

    const confirmDelete = async () => {
        if (!deleteTarget || !deleteReason.trim()) return;
        setDeleting(true);
        try {
            await api.post(`/billing/bank-transactions/${deleteTarget.id}/delete`, { reason: deleteReason.trim() });
            toast.success("Transaction deleted (soft)");
            setDeleteTarget(null);
            setDeleteReason("");
            load();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to delete"));
        } finally {
            setDeleting(false);
        }
    };

    const clearFilters = () => {
        setQ(""); setTypeFilter(""); setModeFilter(""); setCategoryFilter("");
        setDateFrom(""); setDateTo(""); setIncludeDeleted(false);
    };
    const filtersActive = Boolean(q || typeFilter || modeFilter || categoryFilter || dateFrom || dateTo || includeDeleted);

    const [exporting, setExporting] = useState(false);
    const exportCsv = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            // Use raw fetch so the response is treated as a Blob, not parsed
            // as JSON. Forward current filters via the same query params.
            const qs = new URLSearchParams(params).toString();
            const url = `${API_BASE}/billing/bank-transactions/export.csv${qs ? `?${qs}` : ""}`;
            const resp = await fetch(url, {
                method: "GET",
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!resp.ok) {
                let detail = "Export failed";
                try { detail = (await resp.json()).detail || detail; } catch { /* not JSON */ }
                throw new Error(detail);
            }
            const rowCount = resp.headers.get("x-row-count") || items.length;
            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            a.download = `reedos-bank-transactions-${today}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
            toast.success(`Exported ${rowCount} transaction${rowCount === "1" ? "" : "s"}`);
        } catch (err) {
            toast.error(err?.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="bank-transactions-page">
            <PageHeader
                eyebrow="Accounts · Operational ledger"
                title="Bank Transactions"
                description="Lightweight UPI / bank / cash movement log — expenses, refunds, vendor payments and reimbursements. Searchable by UTR for fast reconciliation."
                actions={
                    <>
                        <button
                            type="button"
                            onClick={exportCsv}
                            disabled={exporting || items.length === 0}
                            data-testid="bank-txn-export-btn"
                            title={items.length === 0 ? "No rows to export" : "Download current filtered view as CSV"}
                            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold bg-white border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <DownloadSimple size={14} weight="bold" />
                            {exporting ? "Exporting…" : "Export CSV"}
                        </button>
                        <button
                            type="button"
                            onClick={openCreate}
                            data-testid="bank-txn-create-btn"
                            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors"
                        >
                            <Plus size={14} weight="bold" /> Record transaction
                        </button>
                    </>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-5">
                {/* Summary tiles */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <SummaryTile label="Transactions" value={summary.count} testId="btx-stat-count" />
                    <SummaryTile
                        label="Inflow (filtered)"
                        value={formatINR(summary.inflow, { compact: true })}
                        valueClassName="text-emerald-700"
                        icon={<ArrowDown size={14} weight="bold" className="text-emerald-700" />}
                        testId="btx-stat-inflow"
                    />
                    <SummaryTile
                        label="Outflow (filtered)"
                        value={formatINR(summary.outflow, { compact: true })}
                        valueClassName="text-rose-700"
                        icon={<ArrowUp size={14} weight="bold" className="text-rose-700" />}
                        testId="btx-stat-outflow"
                    />
                    <SummaryTile
                        label="Net (filtered)"
                        value={formatINR(summary.net, { compact: true })}
                        valueClassName={summary.net >= 0 ? "text-emerald-700" : "text-rose-700"}
                        testId="btx-stat-net"
                    />
                </div>

                {/* Filters bar */}
                <div className="reedos-card p-4 space-y-3" data-testid="btx-filters">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-[260px]">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search by UTR / Reference / Counterparty / Remark"
                                data-testid="btx-search"
                                className="w-full h-9 pl-9 pr-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                            />
                        </div>
                        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="btx-type-filter">
                            <option value="">All types</option>
                            {meta.txn_types.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="btx-mode-filter">
                            <option value="">All modes</option>
                            {meta.payment_modes.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="btx-category-filter">
                            <option value="">All categories</option>
                            {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Date</div>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            data-testid="btx-date-from"
                            className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        />
                        <span className="text-xs text-slate-400">to</span>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            data-testid="btx-date-to"
                            className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white"
                        />
                        <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeDeleted}
                                onChange={(e) => setIncludeDeleted(e.target.checked)}
                                data-testid="btx-include-deleted"
                                className="rounded border-slate-300"
                            />
                            Include deleted
                        </label>
                        {filtersActive && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                data-testid="btx-clear-filters"
                                className="ml-auto text-xs text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="text-sm text-slate-500" data-testid="btx-loading">Loading transactions…</div>
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={<Bank size={40} weight="thin" />}
                        title={filtersActive ? "No transactions match these filters" : "No bank transactions yet"}
                        description={
                            filtersActive
                                ? "Try clearing the filters or adjusting the date range."
                                : "Record your first UPI / bank / cash transaction to start building the operational ledger."
                        }
                        data-testid="btx-empty"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="btx-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Date</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Type</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Counterparty</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Category</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Mode</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Reference / UTR</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                    {canEdit && <th className="px-5 py-3 reedos-eyebrow text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((t) => (
                                    <tr
                                        key={t.id}
                                        data-testid={`btx-row-${t.id}`}
                                        className={`border-t border-slate-100 reedos-row-hover ${t.is_deleted ? "opacity-50" : ""}`}
                                    >
                                        <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">
                                            {formatDate(t.txn_date)}
                                            {t.is_deleted && (
                                                <span className="ml-1.5 text-[10px] text-rose-600 font-semibold">DELETED</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            <StatusBadge
                                                status="Draft"
                                                className="!bg-slate-100 !text-slate-700 !border-slate-300"
                                            >
                                                {t.txn_type}
                                            </StatusBadge>
                                        </td>
                                        <td className="px-5 py-3 text-slate-900">
                                            <div className="font-medium">{t.counterparty}</div>
                                            {t.remarks && (
                                                <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{t.remarks}</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-xs text-slate-600">{t.category}</td>
                                        <td className="px-5 py-3 text-xs text-slate-600">{t.payment_mode}</td>
                                        <td className="px-5 py-3 font-mono text-xs text-slate-700">{t.reference_no}</td>
                                        <td className={`px-5 py-3 text-right reedos-data font-semibold whitespace-nowrap ${DIR_STYLE[t.direction]}`}>
                                            {DIR_PREFIX[t.direction]}{formatINR(t.amount)}
                                        </td>
                                        {canEdit && (
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {!t.is_deleted ? (
                                                    <div className="inline-flex items-center gap-1">
                                                        <IconButton
                                                            onClick={() => openEdit(t)}
                                                            testId={`btx-edit-${t.id}`}
                                                            title="Edit"
                                                        >
                                                            <PencilSimple size={14} />
                                                        </IconButton>
                                                        <IconButton
                                                            onClick={() => { setDeleteTarget(t); setDeleteReason(""); }}
                                                            testId={`btx-delete-${t.id}`}
                                                            title="Delete"
                                                            tone="rose"
                                                        >
                                                            <Trash size={14} />
                                                        </IconButton>
                                                    </div>
                                                ) : (
                                                    <span title={t.deleted_reason || ""} className="text-[11px] text-slate-500">
                                                        {t.deleted_reason ? <Eye size={14} className="inline" /> : <EyeSlash size={14} className="inline" />}
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <BankTransactionDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                mode={editTarget ? "edit" : "create"}
                meta={meta}
                existing={editTarget}
                onSaved={load}
            />

            <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
                <AlertDialogContent data-testid="btx-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete bank transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This is a <b>soft delete</b> — the record stays in the database for audit but is hidden from the
                            default view. Capture a short reason so the audit log is meaningful.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {deleteTarget && (
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
                            <div><span className="text-slate-500">Date:</span> <b>{formatDate(deleteTarget.txn_date)}</b></div>
                            <div><span className="text-slate-500">Counterparty:</span> <b>{deleteTarget.counterparty}</b></div>
                            <div><span className="text-slate-500">Amount:</span> <b className={DIR_STYLE[deleteTarget.direction]}>{DIR_PREFIX[deleteTarget.direction]}{formatINR(deleteTarget.amount)}</b></div>
                            <div><span className="text-slate-500">Reference:</span> <b className="font-mono">{deleteTarget.reference_no}</b></div>
                        </div>
                    )}
                    <label className="block">
                        <span className="reedos-eyebrow block mb-1">Reason for deletion *</span>
                        <textarea
                            rows={2}
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            data-testid="btx-delete-reason"
                            className="w-full px-3 py-2 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500 resize-none"
                            placeholder="e.g. Duplicate UTR entry — superseded by BTX-XXXX"
                        />
                    </label>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btx-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="btx-delete-confirm"
                            onClick={confirmDelete}
                            disabled={deleting || deleteReason.trim().length < 3}
                            className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-60"
                        >
                            {deleting ? "Deleting…" : "Confirm delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SummaryTile({ label, value, valueClassName = "", icon, testId }) {
    return (
        <div className="reedos-card p-5" data-testid={testId}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon}{label}</div>
            <div className={`font-display text-3xl font-bold mt-2 reedos-data ${valueClassName}`}>{value}</div>
        </div>
    );
}

function IconButton({ children, onClick, testId, title, tone }) {
    const toneClass = tone === "rose"
        ? "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
        : "text-slate-400 hover:text-slate-900 hover:bg-slate-100";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            title={title}
            className={`p-1.5 rounded transition-colors ${toneClass}`}
        >
            {children}
        </button>
    );
}
